import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Sstv2MigrationFailure } from "shared/sstv2MigrationFailure"
import {
  Sstv2MigrationFailureSource,
  Sstv2MigrationFailureThingTypeMrn,
} from "shared/sstv2MigrationFailureData"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { Sstv2MigrationRow } from "./sstv2MigrationTypes"

/** Rows are diagnostics — cap the render so a 5,000-failure migration can't hang the drawer. */
const MAX_ROWS = 200

type SourceFilter = "all" | Sstv2MigrationFailureSource

const sourceChipColor = (
  s: Sstv2MigrationFailureSource,
): "warning" | "error" => (s === "partner" ? "warning" : "error")

/**
 * "Failed reservations" panel for one migration.
 *
 * Answers the question that previously required reading CloudWatch traces:
 * *which* reservations did not make it, and why. Two distinct kinds are listed
 * together and tagged, because they fail in different places and used to be
 * invisible in different ways:
 *
 *   - `partner` — the reservation was in the event payload's `failure_list`. The
 *     event PROCESSED fine; the partner is reporting it could not migrate these.
 *   - `processing` — our own side threw. For an ACTIVE reservation that means the
 *     durable flip could not rewrite that row (or could not adopt it), and the
 *     execution wrote a failure row rather than throwing — a per-mapping error must
 *     never fail the whole attempt. The migration record's
 *     activeFailed/historyFailed already counted it, but never named it.
 *
 * The raw partner entry is shown per row (that is the "associated payload"), and
 * `traceId` links out for the full envelope, which lives on the trace header
 * rather than being duplicated onto every failure row.
 */
export const Sstv2MigrationFailuresPanel: FC<{ row: Sstv2MigrationRow }> = ({
  row,
}) => {
  const { t } = useTranslation()
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all")

  const query = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetSstv2MigrationFailures,
        "ap.tenantId": row.tenantId,
        "ap.migrationId": row.migrationId,
        "ap.newestFirst": true,
        "ap.limit": 1000,
      },
    },
    { skip: !row.tenantId || !row.migrationId },
  )

  const failures = useMemo(() => {
    const things = (query.data?.things ?? []) as Sstv2MigrationFailure[]
    return things.filter(
      (f) => f?.thingTypeMrn === Sstv2MigrationFailureThingTypeMrn,
    )
  }, [query.data])

  const counts = useMemo(() => {
    let partner = 0
    let processing = 0
    for (const f of failures) {
      if (f.source === "partner") partner++
      else if (f.source === "processing") processing++
    }
    return { partner, processing, total: failures.length }
  }, [failures])

  const visible = useMemo(() => {
    const filtered =
      sourceFilter === "all"
        ? failures
        : failures.filter((f) => f.source === sourceFilter)
    return filtered.slice(0, MAX_ROWS)
  }, [failures, sourceFilter])

  const filteredTotal =
    sourceFilter === "all"
      ? counts.total
      : sourceFilter === "partner"
        ? counts.partner
        : counts.processing

  if (query.isLoading) {
    return (
      <Box sx={{ py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">
            {t("migrationFailuresLoading")}
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box data-ui="UI-SL2W-259">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
          {t("migrationFailedReservations")}
        </Typography>
        {counts.total > 0 && (
          <>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("migrationFailuresPartnerCount", {
                count: counts.partner,
              })}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={t("migrationFailuresProcessingCount", {
                count: counts.processing,
              })}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          </>
        )}
      </Stack>

      {counts.total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("migrationNoFailedReservations")}
        </Typography>
      ) : (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sourceFilter}
            onChange={(_e, v) => v && setSourceFilter(v as SourceFilter)}
            sx={{ mb: 1, "& .MuiToggleButton-root": { py: 0.25, fontSize: "0.7rem" } }}
          >
            <ToggleButton value="all">{t("all")}</ToggleButton>
            <ToggleButton value="partner">
              {t("migrationFailureSourcePartner")}
            </ToggleButton>
            <ToggleButton value="processing">
              {t("migrationFailureSourceProcessing")}
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("migrationFailureSource")}</TableCell>
                  <TableCell>{t("migrationFailureStream")}</TableCell>
                  <TableCell>{t("migrationFailureSl1ResId")}</TableCell>
                  <TableCell>{t("migrationFailureSl2ResId")}</TableCell>
                  <TableCell>{t("migrationFailureReason")}</TableCell>
                  <TableCell>{t("migrationFailureWhen")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((f) => (
                  <TableRow key={f.id ?? `${f.source}-${f.sl1ReservationId}`} hover>
                    <TableCell>
                      <Tooltip title={f.event ?? ""}>
                        <Chip
                          size="small"
                          color={sourceChipColor(f.source)}
                          variant="outlined"
                          label={
                            f.source === "partner"
                              ? t("migrationFailureSourcePartner")
                              : t("migrationFailureSourceProcessing")
                          }
                          sx={{ height: 20, fontSize: "0.66rem" }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{f.stream}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                      {f.sl1ReservationId}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                      {/* Empty sl2 id IS the diagnosis for most partner failures. */}
                      {f.sl2ReservationId ? (
                        f.sl2ReservationId
                      ) : (
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.72rem", color: "text.disabled" }}
                        >
                          {t("migrationFailureNoSl2Id")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", maxWidth: 320 }}>
                      {f.reason ?? (
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.72rem", color: "text.disabled" }}
                        >
                          {t("migrationFailureNoReason")}
                        </Typography>
                      )}
                      {f.rawEntry && (
                        <Tooltip title={f.rawEntry}>
                          <Link
                            component="button"
                            underline="hover"
                            sx={{ ml: 1, fontSize: "0.68rem" }}
                          >
                            {t("migrationFailurePayload")}
                          </Link>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                      {f.partnerTimestamp ?? f.recordedAt}
                      {f.traceId && (
                        <Tooltip title={`traceId ${f.traceId}`}>
                          <Typography
                            component="span"
                            sx={{
                              ml: 0.75,
                              fontFamily: "monospace",
                              fontSize: "0.66rem",
                              color: "text.secondary",
                            }}
                          >
                            {f.traceId.slice(0, 8)}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {filteredTotal > visible.length && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, fontSize: "0.75rem" }}
            >
              {t("migrationFailuresTruncated", {
                shown: visible.length,
                total: filteredTotal,
              })}
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

export default Sstv2MigrationFailuresPanel
